<template>
  <br />
  <div class="col-sm-12">
    <!-- Tabla de presentación -->  
    <div class="card">

      <div class="card-header card-header-compact">
        <h3 class="card-header-title">{{ tableTitle }}</h3>
        <div class="card-header-toolbar">
          <small v-if="loading">Cargando datos...</small>
          <small v-if="!loading && tableData.length === 0">No hay registros</small>
          <input v-model="searchQuery" class="form-control form-control-sm" placeholder="Buscar..." style="width:auto" />
          <button type="button" class="btn btn-sm btn-primary" @click="onSearch">Buscar</button>
          <button type="button" class="btn btn-sm btn-secondary" @click="onAddNew">Agregar</button>
        </div>
      </div>

      <div class="card-body">
        <DataTable
          :rows="paginatedData"
          :columns="displayColumns"
          :loading="loading"
          :error="error"
          :imageColumns="['clave']"
          @view="onViewDetails"
          @edit="onEdit"
        />
        <div v-if="error" class="mt-2"><button type="button" class="btn btn-sm btn-outline-primary" @click="retryCatalog">Reintentar</button></div>
      </div>
      <div class="card-footer">
        <div class="d-flex justify-content-between align-items-center mt-2">
          <div>
            <button class="btn btn-sm btn-secondary me-2" :disabled="currentPage === 1"
              @click="prevPage">Anterior</button>
              <button class="btn btn-sm btn-secondary" :disabled="currentPage >= totalPages"
              @click="nextPage">Siguiente</button>            
          </div>
          <div>
            <small>Total de registros: {{ filteredData.length }}</small>
            <small v-if="!loading && filteredData.length > 0"> Mostrando {{ paginatedData.length }} de {{
              filteredData.length
              }}</small>
          </div>
          <div>
            <small>Página {{ currentPage }} de {{ totalPages }}</small>
          </div>
        </div>
      </div>

    </div>
    
  </div>

</template>

<script>
import { useBootsrapStore } from '@/stores/bootsrap';
import catalogListMixin from '@/mixins/catalogListMixin';
import DataTable from '@/components/DataTable.vue';

export default {
  name: 'catalogo',
  mixins: [catalogListMixin],
  components: { DataTable },
  props: {
    tableName: {
      type: String,
      default: 'aseguradoras',
    },
    tableTitle: {
      type: String,
      default: 'Aseguradoras',
    },
    labels: {
      type: Object,
      default: () => ({'id': '#', 'clave': 'Logo', 'claveTexto': 'Clave', 'nombre': 'Nombre'}),
    },
  },  
  data() {
    return {
      tableColumns: [],
      tableData: [],
      loading: false,
      error: null,
      searchQuery: '',
      pageSize: 10,
      currentPage: 1,
    };
  },

  computed: {
    bootstraplist() {
      return useBootsrapStore().bootstrap;
    },

    filteredData() {
      if (!this.searchQuery) return this.tableData;
      const q = this.searchQuery.toLowerCase();
      return this.tableData.filter(item => {
        return Object.keys(item).some(k => {
          const v = item[k];
          return v != null && String(v).toLowerCase().includes(q);
        });
      });
    },

    totalPages() {
      return Math.max(1, Math.ceil(this.filteredData.length / this.pageSize));
    },

    paginatedData() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredData.slice(start, start + this.pageSize);
    },

    displayColumns() {
      // Use tableColumns (which contains {text,value}) but ensure text is friendly
      return this.tableColumns.map(c => ({ text: this.friendlyLabel(c.value), value: c.value }));
    },
  },

  created() {
    // merge any external labels passed via prop
    if (this.labels && Object.keys(this.labels).length) {
      this.columnLabels = { ...this.columnLabels, ...this.labels };
    }
    this.loadData();
  },

  methods: {
    // Los listados llegan paginados: el mixin devuelve todas las filas (catálogos de hasta 500)
    // o la página pedida en modo servidor; `null` si otra carga más reciente la reemplazó.
    async fetchData(page = 1) {
      return this.loadCatalogItems(this.tableName, page);
    },

    getColumns(data) {
      if (!Array.isArray(data) || data.length === 0) return [];
      return Object.keys(data[0]);
    },

    buildTableColumns(data) {
      const columns = this.getColumns(data);
      // Columnas que no se muestran en la tabla
      const excludedColumns = ['isactive', 'created', 'updated'];
      const filteredColumns = columns.filter(col => !excludedColumns.includes(col.toLowerCase()));
      // Definir orden personalizado de columnas
      const columnOrder = ['id', 'clave', 'claveTexto', 'nombre'];
      // Filtrar y ordenar las columnas según columnOrder
      // ('claveTexto' es una columna derivada de 'clave', ver buildTableData)
      const orderedColumns = columnOrder.filter(col =>
        col === 'claveTexto' ? filteredColumns.includes('clave') : filteredColumns.includes(col)
      );
      // Agregar cualquier columna adicional que no esté en columnOrder
      const remainingColumns = filteredColumns.filter(col => !columnOrder.includes(col));
      const finalColumns = [...orderedColumns, ...remainingColumns];
      return finalColumns.map(col => ({ text: this.friendlyLabel(col), value: col }));
    },

    buildTableData(data) {
      return data.map((item, index) => ({ id: index + 1, ...item, claveTexto: item.clave }));
    },

    async loadData(page = 1) {
      const data = await this.fetchData(page);
      if (data === null) return;
      this.tableColumns = this.buildTableColumns(data);
      this.tableData = this.buildTableData(data);
    },

    friendlyLabel(key) {
      // Prefer labels prop mappings (raw key or generated base), fallback to generated label
        if (this.labels && Object.prototype.hasOwnProperty.call(this.labels, key)) return this.labels[key];
        const base = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        if (this.labels && Object.prototype.hasOwnProperty.call(this.labels, base)) return this.labels[base];
        return base;
    },

    isBooleanLike(val) {
      if (typeof val === 'boolean') return true;
      if (typeof val === 'string') {
        const v = val.trim().toLowerCase();
        return v === 'true' || v === 'false';
      }
      return false;
    },

    toBoolean(val) {
      if (typeof val === 'boolean') return val;
      if (typeof val === 'string') return val.trim().toLowerCase() === 'true';
      return Boolean(val);
    },

    // Detect ISO-like date strings
    isIsoDateString(val) {
      if (typeof val !== 'string') return false;
      // simple ISO 8601 and common datetime patterns
      const isoRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z?$/;
      const isoRegexNoT = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;
      return isoRegex.test(val) || isoRegexNoT.test(val);
    },

    formatDateTime(val) {
      if (val == null) return '';
      let d;
      if (val instanceof Date) {
        d = val;
      } else if (this.isIsoDateString(String(val))) {
        d = new Date(val);
      } else {
        // try parseable
        const parsed = Date.parse(val);
        if (!isNaN(parsed)) d = new Date(parsed);
        else return val;
      }

      if (isNaN(d)) return val;

      const pad = n => String(n).padStart(2, '0');
      const day = pad(d.getDate());
      const month = pad(d.getMonth() + 1);
      const year = String(d.getFullYear());
      const hours = pad(d.getHours());
      const minutes = pad(d.getMinutes());
      const seconds = pad(d.getSeconds());
      return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    },

    formatCell(val, key) {
      if (val == null) return '';
      // don't change format for Id columns or those ending in 'Id' (case-insensitive)
      if (key && /(^id$|id$)/i.test(key)) {
        return String(val);
      }

      // if value is object (like nested), stringify minimally
      if (typeof val === 'object') return JSON.stringify(val);
      // Solo formatear como fecha si es una cadena ISO válida
      if (this.isIsoDateString(val)) {
        return this.formatDateTime(val);
      }
      // Devolver cadenas y números sin transformar
      return String(val);
    },

    prevPage() {
      if (this.currentPage > 1) this.currentPage -= 1;
    },

    nextPage() {
      if (this.currentPage < this.totalPages) this.currentPage += 1;
    },

    onAddNew() {
      // Navegar al detalle de ciudad con id = 0 para crear nuevo registro
      // this.$router.push({ name: 'ciudadesDetail', params: { id: 0 } });
    },

    onViewDetails(item) {
      // Navegar al detalle de producto con el id del registro seleccionado
      // this.$router.push({ name: 'productosDetalle', params: { id: item.id } });
    },

    onEdit(item) {
      // Navegar al detalle de ciudad con el id del registro seleccionado
      //this.$router.push({ name: 'ciudadesDetail', params: { id: item.id } });
    },

  },
};

</script>

<style scoped>
/* Center feather SVG icons inside small action buttons */
.icon-btn {
  width: 30px;
  height: 30px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.icon-btn svg {
  width: 16px;
  height: 16px;
  stroke-width: 2;
}
</style>
